import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useSWR from "swr";
import { z } from "zod";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { DeletableChips } from "../../../../../components/DeletableChips/DeletableChips";
import { HeaderLeft } from "../../../../../components/Layout/Layout";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { RadioButton } from "../../../../../components/RadioButton/RadioButton";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextAreaCounter } from "../../../../../components/TextAreaCounter/TextAreaCounter";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { H6Bold } from "../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../endpoints";
import { Form, RadioButtonContainer } from "../../../../../layout/FormLayout";
import type { DataMutate, OptionType } from "../../../../../types/types";
import type {
  AttributeArgSchema,
  AttributeSchema,
  ListItemSchema,
  ShortListSchema,
} from "../../../../../types/types.PIM";
import {
  TEMPORARY_HIGH_LIMIT,
  removeUnderscore,
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import {
  zodRequiredString,
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../../util/zod.util";
import { Table } from "../../../../../components/Table/Table";
import {
  getAddressTableColumns,
  getAdvancedAddressFields,
  getStandardAddressFields,
} from "./AddressAttributes/address.utils";
import { ObjectReference } from "../../../../../components/ObjectReference/ObjectReference";
import {
  getDefaultPreviewAttributesForObjectType,
  getObjectReferenceAttributeFilters,
  shouldShowObjectReferenceAttribute,
} from "../../../../../components/ObjectReference/utils";

const CreateAttributeSchema = z.object({
  name: z.string(),
  display_name: z.string().optional(),
  description: z.string().optional(),
  input_type: zodSelectBoxType,
  list_id: zodSelectBoxType.optional(),
  choices: zodSelectBoxType.optional(),
  display_configuration: z.enum(["link", "product_card"]).optional(),
  source_object_type: zodSelectBoxType.optional(),
  target_object_type: zodSelectBoxType.optional(),
  target_side_display_name: z.string().optional(),
});

export const CreateAttributeSchemaFn = (
  t: (s: string) => string,
  shouldHaveChoices?: boolean
) =>
  z
    .object({
      name: zodRequiredString(t),
      display_name: z.string().optional(),
      description: z.string().max(200, t("Exceeded maximum limit")).optional(),
      input_type: zodSelectBoxDefault(t),
      list_id: zodSelectBoxDefault(t).optional(),
      choices: zodSelectBoxDefault(t).optional(),
      icons_enabled: z.boolean().optional(),
      size: z.enum(["small", "medium", "large"]).optional(),
      display_configuration: z.enum(["link", "product_card"]).optional(),
      source_object_type: zodSelectBoxType.optional(),
      target_object_type: zodSelectBoxType.optional(),
      target_side_display_name: z.string().optional(),
    })
    .superRefine(
      (
        { input_type, list_id, source_object_type, target_object_type },
        ctx
      ) => {
        if (shouldHaveChoices === false) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Select at least one list item"),
            path: ["choices"],
          });
        }
        if (
          (input_type.value === "single_select" ||
            input_type.value === "multi_select") &&
          !list_id?.value
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("This is a required field"),
            path: ["list_id"],
          });
        }
        if (
          input_type.value === "object_reference" &&
          !source_object_type?.value
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("This is a required field"),
            path: ["source_object_type"],
          });
        }
        if (
          input_type.value === "object_reference" &&
          !target_object_type?.value
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("This is a required field"),
            path: ["target_object_type"],
          });
        }
      }
    );

export const MarginBottomHeaderLeft = styled(HeaderLeft)`
  margin-bottom: 28px;
`;

export const MarginBottomH6 = styled(H6Bold)`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

export type FormValues = z.infer<typeof CreateAttributeSchema>;

export const listToOptions = ({
  id,
  display_name,
  name,
}: ShortListSchema): OptionType<string> => ({
  label: toTitleCase(display_name ? display_name : name),
  value: id,
});

export const shouldUpdateDisplayName = (
  name: string,
  displayName: string | undefined,
  isDisplayNameDirty: boolean
) => {
  return name !== displayName && !isDisplayNameDirty;
};

export const CreateAttribute = ({
  refreshAttributesList,
  onComplete,
  onCancel,
}: {
  refreshAttributesList: DataMutate<AttributeSchema[]>;
  onComplete: () => void;
  onCancel: () => void;
}) => {
  const [choices, setChoices] = useState<Set<string>>();
  const [selectChoices, setSelectChoices] = useState(false);
  const [productReferenceType, setProductReferenceType] =
    useState<string>("single");
  const [addressType, setAddressType] = useState<string>("standard");
  const [userReferenceType, setUserReferenceType] = useState<string>("single");
  const [objectReferenceType, setObjectReferenceType] =
    useState<string>("single");
  const [sourcePreviewAttributes, setSourcePreviewAttributes] = useState<
    Map<string, string>
  >(new Map());
  const [targetPreviewAttributes, setTargetPreviewAttributes] = useState<
    Map<string, string>
  >(new Map());
  const [displayConfiguration, setDisplayConfiguration] =
    useState<string>("link");
  const [submitting, setSubmitting] = useState(false);
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const standardFields = getStandardAddressFields(t);
  const advancedFields = getAdvancedAddressFields(t);
  const addressTableColumns = getAddressTableColumns(t);
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      CreateAttributeSchemaFn(
        t,
        selectChoices ? (choices?.size ?? 0) > 0 : undefined
      )
    ),
    defaultValues: {
      input_type: { label: undefined, value: undefined },
    },
  });
  const {
    handleSubmit,
    register,
    formState,
    control,
    errors,
    watch,
    setValue,
  } = methodsOfUseForm;
  const { data: typesResponse } = useSWR<{ data: string[] }>(
    endpoints.v2_pim_types()
  );
  const {
    name: nameValue,
    display_name: displayNameValue,
    input_type: inputValue,
    list_id: listValue,
    source_object_type: sourceObjectTypeValue,
    target_object_type: targetObjectTypeValue,
  } = watch([
    "name",
    "display_name",
    "input_type",
    "list_id",
    "source_object_type",
    "target_object_type",
  ]);

  const getDefaultAttributesEndpoint = (objectType?: string) => {
    if (!objectType) {
      return null;
    }
    const params = new URLSearchParams();
    params.set("limit", String(TEMPORARY_HIGH_LIMIT));
    params.set("offset", "0");
    const attributeNames = getObjectReferenceAttributeFilters(objectType);
    if (attributeNames.length === 0) {
      return null;
    }
    attributeNames.forEach((name) => params.append("names", name));
    return `${endpoints.v2_tenants_id_pim_attributes(
      tenant_id
    )}?${params.toString()}`;
  };

  const { data: sourceDefaultAttributesResponse } = useSWR<{
    data: AttributeSchema[];
  }>(
    inputValue?.value === "object_reference"
      ? getDefaultAttributesEndpoint(sourceObjectTypeValue?.value)
      : null
  );
  const { data: targetDefaultAttributesResponse } = useSWR<{
    data: AttributeSchema[];
  }>(
    inputValue?.value === "object_reference"
      ? getDefaultAttributesEndpoint(targetObjectTypeValue?.value)
      : null
  );
  if (
    shouldUpdateDisplayName(
      nameValue,
      displayNameValue,
      !!formState.dirtyFields["display_name"]
    )
  ) {
    setValue("display_name", nameValue);
  }
  const showList = () =>
    inputValue.value === "single_select" || inputValue.value === "multi_select";
  const onSubmit = async ({
    name,
    display_name,
    description,
    input_type,
    list_id,
    source_object_type,
    target_object_type,
    target_side_display_name,
  }: FormValues): Promise<void> => {
    setSubmitting(true);
    name = name.trim();
    if (display_name) {
      display_name = display_name.trim();
    }
    try {
      await Axios.post<AttributeArgSchema, AttributeSchema>(
        endpoints.v2_tenants_id_pim_attributes(tenant_id),
        {
          name,
          display_name,
          description,
          input_type: input_type.value,
          address_mode:
            input_type.value === "address" ? addressType : undefined,
          is_multiple_choice:
            input_type.value === "single_select" ||
            input_type.value === "multi_select" ||
            (input_type.value === "product_reference" &&
              productReferenceType === "multi") ||
            (input_type.value === "user" && userReferenceType === "multi") ||
            (input_type.value === "object_reference" &&
              objectReferenceType === "multi"),
          choices: selectChoices ? Array.from(choices ?? []) : undefined,
          list_id: list_id?.value,
          display_configuration:
            input_type.value === "product_reference"
              ? displayConfiguration
              : input_type.value === "object_reference" &&
                target_object_type?.value === "product"
              ? displayConfiguration
              : undefined,
          source_object_type:
            input_type.value === "object_reference"
              ? source_object_type?.value
              : undefined,
          target_object_type:
            input_type.value === "object_reference"
              ? target_object_type?.value
              : undefined,
          target_side_display_name:
            input_type.value === "object_reference"
              ? target_side_display_name
              : undefined,
          source_preview_attribute_ids:
            input_type.value === "object_reference"
              ? Array.from(sourcePreviewAttributes.values())
              : undefined,
          target_preview_attribute_ids:
            input_type.value === "object_reference"
              ? Array.from(targetPreviewAttributes.values())
              : undefined,
        }
      );
      await refreshAttributesList();
      notifySuccess(t("Attribute successfully created"));
      onComplete();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not create attribute. Something went wrong."),
        {
          error,
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const changeProductReferenceType = (
    e: React.FormEvent<HTMLSelectElement>
  ) => {
    if (e.currentTarget.value) {
      setProductReferenceType(e.currentTarget.value);
    }
  };

  const changeAddressReferenceType = (
    e: React.FormEvent<HTMLSelectElement>
  ) => {
    if (e.currentTarget.value) {
      setAddressType(e.currentTarget.value);
    }
  };

  const changeDisplayConfiguration = (
    e: React.FormEvent<HTMLSelectElement>
  ) => {
    if (e.currentTarget.value) {
      setDisplayConfiguration(e.currentTarget.value);
    }
  };
  const changeUserReferenceType = (e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value) {
      setUserReferenceType(e.currentTarget.value);
    }
  };

  const changeObjectReferenceType = (e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value) {
      setObjectReferenceType(e.currentTarget.value);
    }
  };

  const removeChoice = (name: string) =>
    setChoices((prev) => {
      const newSet = new Set(prev);
      newSet.delete(name);
      return newSet;
    });

  const addToChoices = ({ label }: OptionType<string>) =>
    setChoices((prev) => {
      if (prev) {
        const newSet = new Set(prev);
        newSet.add(label);
        return newSet;
      } else {
        return new Set([label]);
      }
    });

  const addSourcePreviewAttribute = ({ label, value }: OptionType<string>) => {
    if (!label || !value) {
      return;
    }
    setSourcePreviewAttributes((prev) => {
      const next = new Map(prev);
      next.set(label, value);
      return next;
    });
  };

  const addTargetPreviewAttribute = ({ label, value }: OptionType<string>) => {
    if (!label || !value) {
      return;
    }
    setTargetPreviewAttributes((prev) => {
      const next = new Map(prev);
      next.set(label, value);
      return next;
    });
  };

  const removeSourcePreviewAttribute = (name: string) => {
    setSourcePreviewAttributes((prev) => {
      const next = new Map(prev);
      next.delete(name);
      return next;
    });
  };

  const removeTargetPreviewAttribute = (name: string) => {
    setTargetPreviewAttributes((prev) => {
      const next = new Map(prev);
      next.delete(name);
      return next;
    });
  };

  useEffect(() => {
    setChoices(new Set([]));
  }, [listValue]);

  useEffect(() => {
    if (inputValue?.value !== "object_reference") {
      setSourcePreviewAttributes(new Map());
      setTargetPreviewAttributes(new Map());
    }
  }, [inputValue]);

  useEffect(() => {
    if (
      inputValue?.value !== "object_reference" ||
      !sourceObjectTypeValue?.value
    ) {
      setSourcePreviewAttributes(new Map());
      return;
    }

    if (!sourceDefaultAttributesResponse) {
      setSourcePreviewAttributes(new Map());
      return;
    }
    setSourcePreviewAttributes(
      getDefaultPreviewAttributesForObjectType(
        sourceDefaultAttributesResponse.data ?? [],
        sourceObjectTypeValue?.value
      )
    );
  }, [
    inputValue,
    sourceDefaultAttributesResponse,
    sourceObjectTypeValue?.value,
  ]);

  useEffect(() => {
    if (
      inputValue?.value !== "object_reference" ||
      !targetObjectTypeValue?.value
    ) {
      setTargetPreviewAttributes(new Map());
      return;
    }

    if (!targetDefaultAttributesResponse) {
      setTargetPreviewAttributes(new Map());
      return;
    }
    setTargetPreviewAttributes(
      getDefaultPreviewAttributesForObjectType(
        targetDefaultAttributesResponse.data ?? [],
        targetObjectTypeValue?.value
      )
    );
  }, [
    inputValue,
    targetDefaultAttributesResponse,
    targetObjectTypeValue?.value,
  ]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <MarginBottomH6>{t("Attribute Definition")}</MarginBottomH6>
        <TextField
          name="name"
          label={t("Attribute Name")}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        <TextField
          name="display_name"
          label={t("Display Name")}
          theref={register({
            required: false,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        <TextAreaCounter
          name="description"
          label={t("Short Description")}
          theref={register({
            required: false,
          })}
          formState={formState}
          errors={errors}
          watch={watch}
        />
        <MarginBottomH6>{t("Attribute Type")}</MarginBottomH6>
        {typesResponse && (
          <>
            <Controller
              as={SelectBoxV2}
              control={control}
              name="input_type"
              autoComplete={"input_type"}
              placeholder={t("Attribute Type")}
              options={typesResponse.data
                .filter((type) =>
                  shouldShowObjectReferenceAttribute({
                    inputType: type,
                    objectReferenceEnabled: object_reference_enabled,
                  })
                )
                .map((type) => ({
                  label: toTitleCase(removeUnderscore(type)),
                  value: type,
                }))}
              rules={{
                required: true,
              }}
              errors={errors}
              formState={formState}
            />
            {inputValue.value === "product_reference" && (
              <>
                <RadioButtonContainer>
                  <RadioButton
                    name="product_reference_type"
                    value="single"
                    checked={productReferenceType === "single"}
                    optionTitle={t("Single Reference")}
                    handleChange={changeProductReferenceType}
                  />
                  <RadioButton
                    name="product_reference_type"
                    value="multi"
                    checked={productReferenceType === "multi"}
                    optionTitle={t("Multi Reference")}
                    handleChange={changeProductReferenceType}
                  />
                </RadioButtonContainer>
                <MarginBottomH6>{t("Display Configuration")}</MarginBottomH6>
                <RadioButtonContainer>
                  <RadioButton
                    name="display_configuration"
                    value="link"
                    checked={displayConfiguration === "link"}
                    optionTitle={t("Link")}
                    handleChange={changeDisplayConfiguration}
                  />
                  <RadioButton
                    name="display_configuration"
                    value="product_card"
                    checked={displayConfiguration === "product_card"}
                    optionTitle={t("Product Card")}
                    handleChange={changeDisplayConfiguration}
                  />
                </RadioButtonContainer>
              </>
            )}
            {inputValue.value === "user" && (
              <RadioButtonContainer>
                <RadioButton
                  name="user_reference_type"
                  value="single"
                  checked={userReferenceType === "single"}
                  optionTitle="Single User"
                  handleChange={changeUserReferenceType}
                />
                <RadioButton
                  name="user_reference_type"
                  value="multi"
                  checked={userReferenceType === "multi"}
                  optionTitle="Multiple Users"
                  handleChange={changeUserReferenceType}
                />
              </RadioButtonContainer>
            )}
            {inputValue.value === "address" && (
              <>
                <RadioButtonContainer>
                  <RadioButton
                    name="address_reference_type"
                    value="standard"
                    checked={addressType === "standard"}
                    optionTitle="Standard"
                    handleChange={changeAddressReferenceType}
                  />
                  <RadioButton
                    name="address_reference_type"
                    value="advanced"
                    checked={addressType === "advanced"}
                    optionTitle="Advanced"
                    handleChange={changeAddressReferenceType}
                  />
                </RadioButtonContainer>

                <Table
                  columns={addressTableColumns}
                  data={
                    addressType === "advanced" ? advancedFields : standardFields
                  }
                  isLoading={false}
                  error={undefined}
                  lastChildleftAlign={true}
                />
              </>
            )}
            {inputValue.value === "object_reference" && (
              <>
                <ObjectReference
                  objectReferenceType={objectReferenceType}
                  onObjectReferenceTypeChange={changeObjectReferenceType}
                  tenantId={tenant_id}
                  control={control}
                  errors={errors}
                  formState={formState}
                  register={register}
                  addSourcePreviewAttribute={addSourcePreviewAttribute}
                  addTargetPreviewAttribute={addTargetPreviewAttribute}
                  removeSourcePreviewAttribute={removeSourcePreviewAttribute}
                  removeTargetPreviewAttribute={removeTargetPreviewAttribute}
                  sourcePreviewChips={Array.from(
                    sourcePreviewAttributes.keys()
                  ).map((name) => ({ name }))}
                  targetPreviewChips={Array.from(
                    targetPreviewAttributes.keys()
                  ).map((name) => ({ name }))}
                  sourceObjectType={sourceObjectTypeValue?.value}
                  targetObjectType={targetObjectTypeValue?.value}
                />
              </>
            )}
          </>
        )}
        {showList() && (
          <>
            <Controller
              as={SearchSelectInfiniteScroll}
              control={control}
              name="list_id"
              autoComplete="on"
              baseUrl={endpoints.v2_tenants_id_or_slug_pim_lists(tenant_id)}
              params={(() => {
                const params = new URLSearchParams();
                params.append("status", "active");
                return params;
              })()}
              getOptions={(response: ShortListSchema[]) =>
                response.reduce(
                  (
                    prev: { label: string; value: string }[],
                    cur: ShortListSchema
                  ) => {
                    if (!Boolean(cur.parent)) {
                      prev.push(listToOptions(cur));
                    }
                    return prev;
                  },
                  []
                )
              }
              placeholder={t("Choose list")}
              testid="list_id_search"
              defaultOptions
              errors={errors}
              formState={formState}
            />
            {listValue && (
              <>
                <ToggleSwitchV2
                  label={t("Use only specific values")}
                  onChange={(e) => setSelectChoices(e.currentTarget.checked)}
                  name="specific_values"
                  defaultChecked={selectChoices}
                />
                {selectChoices && (
                  <>
                    <SearchSelectInfiniteScroll
                      name="choices"
                      params={(() => {
                        const params = new URLSearchParams();
                        params.append("status", "active");
                        return params;
                      })()}
                      baseUrl={endpoints.v2_tenants_id_or_slug_pim_lists_id_items(
                        tenant_id,
                        listValue!.value
                      )}
                      getOptions={(response: ListItemSchema[]) =>
                        response.reduce(
                          (
                            prev: { label: string; value: string }[],
                            { name, id }: ListItemSchema
                          ) => [
                            ...prev,
                            { label: toTitleCase(name), value: id },
                          ],
                          []
                        )
                      }
                      onChange={addToChoices}
                      errors={errors}
                      formState={formState}
                      placeholder={t("Search list items from {{listName}}", {
                        listName: listValue?.label,
                      })}
                      // This prevents a value from ever appearing in the input,
                      // instead the value is added to the chips.
                      value={null}
                      testid="list_item_id_search"
                      autoComplete="on"
                    />
                    <DeletableChips
                      chips={
                        Array.from(choices ?? [])?.map?.((choice) => ({
                          name: choice,
                        })) ?? []
                      }
                      noneSelectedName="choices"
                      handleClick={removeChoice}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2
            fullWidth
            disabled={submitting}
            type="reset"
            onClick={onCancel}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth type="submit" loading={submitting}>
            {t("Create")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
};
